import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { BillingService } from '@galaxy/billing';
import { Frequency } from '@vendasta/galaxy/frequency';
import { MarketplacePackagesApiService } from '@vendasta/marketplace-packages';
import { Currency, DurationInterface, RevenuePeriod } from '@vendasta/sales-orders';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { convertSalesOrderCurrencyToString, salesOrderPeriodToBillingFrequency } from '../../shared/conversion-utils';
import { StoreItem } from '../../store-item';
import { getCurrencyFromLineItem, getStorePriceForApp, getStorePriceForPackage } from './discount-helpers';
import { TaxOption, UILineItem } from './interface';

@Component({
  selector: 'store-order-item-list',
  templateUrl: './order-item-list.component.html',
  styleUrls: ['./order-item-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderItemListComponent implements OnInit {
  @Input() partnerId: string;
  @Input() marketId: string;
  @Input() showPricing = true;
  @Input()
  set orderItems(items: UILineItem[]) {
    this.lineItems$$.next(items);
  }
  // @deprecated: this does nothing.
  @Input() taxOptions: TaxOption[];
  // @deprecated: this does nothing.
  @Input() duration: DurationInterface;
  // @deprecated: this does nothing.
  @Input() hidePricing: boolean;

  private lineItems$$: ReplaySubject<UILineItem[]> = new ReplaySubject<UILineItem[]>(1);
  storeItems$: Observable<StoreItem[]>;

  constructor(
    protected billingService: BillingService,
    protected marketplacePackages: MarketplacePackagesApiService,
  ) {}

  ngOnInit(): void {
    const appStorePrices$ = this.lineItems$$.pipe(
      map((items) => items?.map((item) => item.billingId) || []),
      map((billingAppIds) => billingAppIds.filter((billingAppId) => !!billingAppId)),
      mergeMap((billingAppIds) => {
        if (billingAppIds.length === 0) {
          return of({});
        }
        return this.billingService.getMultiRetailPricing(this.partnerId, null, billingAppIds, this.marketId);
      }),
      catchError(() => of({})),
    );

    const packageStorePrices$ = this.lineItems$$.pipe(
      map((items) => items?.filter((item) => !!item.lineItem.packageId).map((item) => item.lineItem.packageId) || []),
      mergeMap((packageIds) => {
        if (packageIds.length === 0) {
          return of({ packageContainers: [] });
        }
        return this.marketplacePackages.getMultiPackages({
          packageIds: packageIds,
        });
      }),
      map((response) => {
        if (!response.packageContainers) {
          return {};
        }
        return response.packageContainers.reduce((container, obj) => {
          container[obj.package.packageId] = obj.package.pricing;
          return container;
        }, {});
      }),
      catchError(() => of({})),
    );

    this.storeItems$ = combineLatest([this.lineItems$$, appStorePrices$, packageStorePrices$]).pipe(
      filter(([lineItems]) => !!lineItems),
      map(([items, appStorePrices, packageStorePrices]) =>
        (items || []).map((i) => {
          const editionLabel = i.editionName ? ` | ${i.editionName}` : '';
          const countLabel = i.lineItem.quantity > 1 ? ` (${i.lineItem.quantity})` : '';
          let totalPrice = 0;

          const prices = (i.lineItem?.currentRevenue?.revenueComponents || []).map((revenueComponent) => {
            totalPrice += revenueComponent.value || 0;
            const frequency = salesOrderPeriodToBillingFrequency(revenueComponent.period);

            let storePrice: number;
            const lineItemCurrency = getCurrencyFromLineItem(i);
            if (i?.lineItem?.packageId) {
              storePrice = getStorePriceForPackage(
                i.lineItem.packageId,
                lineItemCurrency,
                revenueComponent.period,
                packageStorePrices,
              );
            } else {
              storePrice = getStorePriceForApp(i.billingId, lineItemCurrency, revenueComponent.period, appStorePrices);
            }
            // if there isn't a valid store price (its less than zero or null) the price will be set to null
            storePrice = storePrice !== null && storePrice >= 0 ? storePrice : null;
            return {
              price: revenueComponent.value,
              priceInDollars: revenueComponent.value / 100,
              frequency: frequency,
              formattedFrequency: frequency[0].toUpperCase() + frequency.slice(1),
              isStartingPrice: revenueComponent.isStartingRevenue,
              storePrice: storePrice,
            };
          });
          const revenueComponents = i.lineItem?.currentRevenue?.revenueComponents ?? [];
          let revenuePeriod = RevenuePeriod.MONTHLY;
          if (revenueComponents.length > 0) {
            revenuePeriod = revenueComponents[0]?.period ?? RevenuePeriod.MONTHLY;
          }
          return {
            name: `${i.name}${editionLabel}${countLabel}`,
            tagline: '',
            headerImageUrl: i.bannerUrl,
            iconUrl: i.iconUrl,
            formattedPrice: '',
            pricing: {
              prices: prices,
              totalPriceInDollars: totalPrice / 100,
              currency: this.getCurrency(i.lineItem),
              totalStorePriceInDollars: prices.reduce((sum, current) => sum + current.storePrice, 0) / 100,
            },
            parentName: i.parentName,
            billingPeriod: i.lineItem?.billingPeriod,
            revenuePeriod: revenuePeriod,
          };
        }),
      ),
    );
  }

  getCurrency(item: { currency: Currency; currencyCode: string }): string {
    if (item.currencyCode) {
      return item.currencyCode;
    }
    return convertSalesOrderCurrencyToString(item.currency);
  }

  protected readonly Frequency = Frequency;
}
